import axios from "axios";
import { processEncryptedResponse } from "../utils/ResponseDecreypt";
import useExpiredToken from "../utils/useExpiredToken";
import { useCallback, useEffect, useState, useRef } from "react";
import { useToast } from "../utils/Toast";
import { useSelector } from "react-redux";
import { useLoader } from "../contexts/LoaderContext";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
const showDecryptedLogs = process.env.REACT_APP_LOG_DECRYPTED_DATA === "true";

class AbortControllerManager {
  constructor() {
    this.controllers = new Map();
  }

  generateKey(endpoint, params = {}, method = 'get') {
    return `${method}:${endpoint}:${JSON.stringify(params)}`;
  }

  register(key, controller) {
    if (this.controllers.has(key)) {
      this.controllers.get(key).abort();
      this.controllers.delete(key);
    }
    this.controllers.set(key, controller);
    return key;
  }

  cancel(key) {
    if (this.controllers.has(key)) {
      this.controllers.get(key).abort();
      this.controllers.delete(key);
    }
  }

  cancelAll() {
    for (const [, controller] of this.controllers) {
      controller.abort();
    }
    this.controllers.clear();
  }

  complete(key) {
    this.controllers.delete(key);
  }
}

const abortManager = new AbortControllerManager();

export const getApi = async (endpoint, token, expireToken, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.get(`${BASE_URL}/${endpoint}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("getApi response data:", decryptedData);
    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    console.error("Login API error:", error.response);
    if (error.status === 401) {
      expireToken();
    }
    return false;
  }
};

export const actionPost = async (endpoint, token, action, data, expireToken, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.post(
      `${BASE_URL}/${endpoint}`,
      {
        action,
        input: data,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      },
    );
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("actionPost response data:", decryptedData);

    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    console.error("Login API error:::::::::::::::::", error.response?.data);
    if (error.status === 401) {
      expireToken();
    }
    return error.response.data;
  }
};

export const logOut = async (endpoint, token, data, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.post(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    if (res) {
      return res.data;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    console.error("Login API error:", error.response);
    return false;
  }
};

export const assignPost = async (endpoint, token, data, expireToken, toast = null, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.post(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("assignPost response data:", decryptedData);
    toast && toast.success(decryptedData?.message || decryptedData);
    if (res.status) {
      return res;
    } else {
      return res;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    const decryptedData = await processEncryptedResponse(error.response?.data);
    showDecryptedLogs && console.log("assignPost response data:", decryptedData);
    toast && toast.error(decryptedData?.message || decryptedData);
    if (error.status === 401) {
      expireToken();
    }
    return error.response;
  }
};

export const assignPatch = async (endpoint, token, data, expireToken, toast = null, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.put(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("assignPatch response data:", decryptedData);
    toast && toast.success(decryptedData?.message || decryptedData);
    if (res.status) {
      return res;
    } else {
      return res;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    const decryptedData = await processEncryptedResponse(error.response?.data);
    showDecryptedLogs && console.log("assignPatch response data:", decryptedData);
    toast && toast.error(decryptedData?.message || decryptedData);
    if (error.status === 401) {
      expireToken();
    }
    return error.response;
  }
};

export const deleteApi = async (endpoint, token, expireToken, options = {}) => {
  const { signal } = options;
  try {
    const res = await axios.delete(`${BASE_URL}/${endpoint}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { isCancelled: true };
    }
    console.error("Login API error:", error.response);
    if (error.status === 401) {
      expireToken();
    }
    return false;
  }
};

export const useApiCall = () => {
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const toast = useToast();
  const expireToken = useExpiredToken();
  const token = useSelector((state) => state.auth.jwtToken);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortManager.cancelAll();
    };
  }, []);

  const callApi = useCallback(
    async ({
      endpoint = "",
      bodyData = {},
      queryParams = {},
      showToast = true,
      apiMethod = "get",
      useToken = true,
      loader = true,
      abortPrevious = true,
      requestKey = null,
    }) => {
      const key = requestKey || abortManager.generateKey(endpoint, { ...queryParams, ...bodyData }, apiMethod);
      
      if (abortPrevious) {
        abortManager.cancel(key);
      }

      loader && showLoader();
      setLoading(true);
      
      const controller = new AbortController();
      abortManager.register(key, controller);

      try {
        const method = apiMethod.toLowerCase();
        const url = `${BASE_URL.replace(/\/$/, "")}/${endpoint.replace(/^\//, "")}`;

        const config = {
          headers: { ...(useToken && { Authorization: `Bearer ${token}` }) },
          params: queryParams,
          signal: controller.signal,
        };

        let res, status, message;
        if (["get", "delete"].includes(method)) {
          res = await axios[method](url, config);
        } else {
          res = await axios[method](url, bodyData, config);
        }

        if (!isMountedRef.current) {
          return { isCancelled: true };
        }

        const decryptedData = await processEncryptedResponse(res.data);
        showDecryptedLogs &&
          console.log("apiCall decrypted response:", decryptedData, typeof decryptedData);

        if (decryptedData?.status !== undefined) {
          status = decryptedData.status;
        } else if (res.data?.status !== undefined) {
          status = res.data?.status;
        } else {
          status = true;
        }

        if (decryptedData) {
          message = decryptedData?.message || decryptedData;
        } else if (typeof res?.data?.message === "string" || typeof res?.data?.data === "string") {
          message = res?.data?.message || res?.data?.data;
        }

        if (showToast) {
          if (status) {
            typeof message === "string" && toast.success(message);
          } else {
            typeof message === "string" && toast.error(message);
          }
        }
        return { response: res, decrypted: decryptedData, status, responseData: res.data };
      } catch (error) {
        if (axios.isCancel(error) || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return { error: 'cancelled', status: false, isCancelled: true };
        }

        if (!isMountedRef.current) {
          return { isCancelled: true };
        }

        const decryptedData = await processEncryptedResponse(error.response?.data);
        showDecryptedLogs && console.log("apiCall error response:", decryptedData);
        if (showToast) {
          if (decryptedData) toast.error(decryptedData?.message || decryptedData);
          if (
            typeof error.response?.data?.message === "string" ||
            typeof error.response?.data?.data === "string"
          )
            toast.error(error.response?.data?.message || error.response?.data?.data);
        }

        if (error.response?.status === 401) {
          expireToken();
        }

        return { error: error.response, decrypted: decryptedData, status: false };
      } finally {
        setLoading(false);
        loader && hideLoader();
        abortManager.complete(key);
      }
    },
    [token, expireToken, toast, showLoader, hideLoader],
  );

  const cancelRequest = useCallback((key) => {
    abortManager.cancel(key);
  }, []);

  const cancelAllRequests = useCallback(() => {
    abortManager.cancelAll();
  }, []);

  return { 
    callApi, 
    loading, 
    cancelRequest, 
    cancelAllRequests
  };
};

export const fetchZones = async (token, expireToken, signal = null) => {
  const encryptedRes = await getApi("superadmin/master/zones", token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchRoles = async (token, expireToken, signal = null) => {
  const encryptedRes = await getApi("superadmin/master/roles", token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchQlUsersByZone = async (zone, roleId, token, expireToken, signal = null) => {
  const type = roleId === 10 ? "tl" : "ql";
  const encryptedRes = await getApi(`superadmin/master/${type}?zone=${zone}`, token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchQlQuestions = async (auditTypeId, token, expireToken, signal = null) => {
  const encryptedRes = await getApi(
    `superadmin/master/ql-questions/${auditTypeId}`,
    token,
    expireToken,
    { signal }
  );
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchAgents = async (token, expireToken, signal = null) => {
  const encryptedRes = await getApi("superadmin/master/agents/list", token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchAuditSummary = async (token,expireToken,payload = {},signal = null,) => {
  const encryptedRes = await actionPost(
    "calls/audit-summary",
    token,
    "READ",
    payload,
    expireToken,
    { signal },
  );
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};


export const fetchQAs = async (token, expireToken, signal = null) => {
  const encryptedRes = await getApi("superadmin/master/qas", token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};


export const fetchCallsOverview = async (token, expireToken, queryParams = {}, signal = null) => {
  const qs = new URLSearchParams(
    Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();
  const endpoint = qs ? `calls/overview?${qs}` : "calls/overview";
  const encryptedRes = await getApi(endpoint, token, expireToken, { signal });
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};


export const fetchAuditDrillDown = async (
  token, expireToken, { metric, role, ...filters } = {}, signal = null
) => {
  const encryptedRes = await actionPost(
    "calls/audit-drilldown", 
    token,
    "READ",
    { metric, role, ...filters },
    expireToken,
    { signal },
  );
  if (encryptedRes?.isCancelled) return { isCancelled: true };
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};